import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Sheet,
  Stack,
  Typography,
  IconButton,
  Link as JoyLink,
} from "@mui/joy";
import { Menu, X, ChevronDown, Zap } from "lucide-react";
import { PRODUCTS } from "../data/products";
const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Products", to: "/products", hasMenu: true },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
];

// Reference the same design-token colors used by the original Tailwind
// theme so no visual values change — only the styling mechanism does.
const colors = {
  ink: "var(--ink)",
  safety: "var(--safety)",
  obsidian: "var(--obsidian)",
  mutedForeground: "var(--muted-foreground)",
  white: "#ffffff",
  // Explicit orange used for active links/fields and hover states
  orange: "#f97316",
};

const monoLabelStyle = {
  fontFamily: "monospace",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Box
      component="header"
      data-testid="site-header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        transition: "background-color 0.3s, border-color 0.3s",
        borderBottom: "1px solid",
        borderColor: scrolled
          ? `rgba(from ${colors.ink} r g b / 0.1)`
          : "transparent",
        backgroundColor: scrolled ? "rgba(255, 255, 255, 0.8)" : "transparent",
        backdropFilter: scrolled ? "blur(24px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(24px)" : "none",
      }}
    >
      <Box
        sx={{
          maxWidth: "80rem",
          marginX: "auto",
          paddingX: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: { xs: "4rem", md: "5rem" } }}
        >
          <JoyLink
            href="/"
            data-testid="logo-link"
            underline="none"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <Box
              className="logo-icon"
              sx={{
                width: { xs: "3rem", md: "4rem" }, // Increase logo container
                height: { xs: "3rem", md: "4rem" },
                display: "grid",
                placeItems: "center",
                overflow: "visible", // or remove this line
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="ITL Logo"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: "1.125rem",
                letterSpacing: "-0.025em",
                lineHeight: 1,
                color: colors.ink,
              }}
            >
              ITL
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: "10px",
                  fontFamily: "monospace",
                  letterSpacing: "0.2em",
                  color: colors.mutedForeground,
                  fontWeight: 500,
                }}
              >
                TECHNICAL SERVICE
              </Box>
            </Typography>
          </JoyLink>

          <Stack
            component="nav"
            direction="row"
            alignItems="center"
            spacing={4}
            sx={{ display: { xs: "none", lg: "flex" } }}
          >
            {NAV.map((item) =>
              item.hasMenu ? (
                <Box
                  key={item.to}
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setProdOpen(true)}
                  onMouseLeave={() => setProdOpen(false)}
                >
                  <JoyLink
                    href={item.to}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    underline="none"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      ...monoLabelStyle,
                      color: pathname.startsWith(item.to)
                        ? colors.orange
                        : colors.ink,
                      transition: "color 0.2s",
                      "&:hover": { color: colors.orange },
                    }}
                  >
                    {item.label}{" "}
                    <ChevronDown
                      style={{ width: "0.75rem", height: "0.75rem" }}
                    />
                  </JoyLink>
                  {prodOpen && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        mt: 0.5, // instead of paddingTop
                        width: "20rem",
                        zIndex: 1000,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#111111", // Black background
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "8px",
                          overflow: "hidden",
                          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
                        }}
                      >
                        {PRODUCTS.map((p) => (
                          <JoyLink
                            key={p.slug}
                            href={`/products/${p.slug}`}
                            underline="none"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              px: 2,
                              py: 1.5,
                              color: "#fff",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#1f1f1f",
                              },
                              "&:hover .product-short": {
                                color: colors.orange,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                color: colors.orange,
                              }}
                            >
                              [{p.index}]
                            </Typography>

                            <Typography
                              className="product-short"
                              sx={{
                                color: "#fff",
                                transition: "color 0.2s",
                              }}
                            >
                              {p.short}
                            </Typography>
                          </JoyLink>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <JoyLink
                  key={item.to}
                  href={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  underline="none"
                  sx={{
                    ...monoLabelStyle,
                    transition: "color 0.2s",
                    color: pathname === item.to ? colors.orange : colors.ink,
                    "&:hover": { color: colors.orange },
                  }}
                >
                  {item.label}
                </JoyLink>
              ),
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <JoyLink
              href="/contact"
              data-testid="nav-quote-cta"
              underline="none"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: colors.orange,
                color: colors.white,
                px: "1.25rem",
                py: "0.625rem",
                ...monoLabelStyle,
                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "#ea580c", // slightly darker orange
                  transform: "translateY(-1px)",
                },
              }}
            >
              Request a Quote
            </JoyLink>
            <IconButton
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((o) => !o)}
              sx={{
                display: { xs: "grid", lg: "none" },
                placeItems: "center",
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: 0,
                backgroundColor: colors.ink,
                color: colors.white,
                "&:hover": { backgroundColor: colors.ink },
              }}
            >
              {open ? (
                <X style={{ width: "1.25rem", height: "1.25rem" }} />
              ) : (
                <Menu style={{ width: "1.25rem", height: "1.25rem" }} />
              )}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {open && (
        <Sheet
          data-testid="mobile-menu"
          variant="plain"
          sx={{
            display: { lg: "none" },
            bgcolor: "#0a0a0a", // Black background
            color: "#fff",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
          }}
        >
          <Stack sx={{ paddingX: "1rem", paddingY: "1.5rem", gap: "0.25rem" }}>
            {/* {NAV.map((item) => (
              <JoyLink
                key={item.to}
                href={item.to}
                underline="none"
                sx={{
                  paddingY: "0.75rem",
                  ...monoLabelStyle,
                  color: pathname === item.to ? colors.orange : colors.white,
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  "&:hover": { color: colors.orange },
                }}
              >
                {item.label}
              </JoyLink>
            ))}
            {PRODUCTS.map((p) => (
              <JoyLink
                key={p.slug}
                href={`/products/${p.slug}`}
                underline="none"
                sx={{
                  paddingY: "0.5rem",
                  paddingLeft: "1rem",
                  fontSize: "0.875rem",
                  color:
                    pathname === `/products/${p.slug}`
                      ? colors.orange
                      : "rgba(255,255,255,0.7)",
                  "&:hover": { color: colors.orange },
                }}
              >
                — {p.short}
              </JoyLink>
            ))} */}
            {NAV.map((item) => (
              <Box key={item.to}>
                <JoyLink
                  href={item.to}
                  underline="none"
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    py: "0.75rem",
                    ...monoLabelStyle,
                    color: pathname === item.to ? colors.orange : colors.white,
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    "&:hover": {
                      color: colors.orange,
                    },
                  }}
                >
                  {item.label}
                </JoyLink>

                {item.hasMenu &&
                  PRODUCTS.map((p) => (
                    <JoyLink
                      key={p.slug}
                      href={`/products/${p.slug}`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        py: "0.5rem",
                        pl: "1.5rem",
                        textAlign: "left",
                        fontSize: "0.875rem",
                        color:
                          pathname === `/products/${p.slug}`
                            ? colors.orange
                            : "rgba(255,255,255,0.7)",
                        "&:hover": {
                          color: colors.orange,
                        },
                      }}
                    >
                      — {p.short}
                    </JoyLink>
                  ))}
              </Box>
            ))}
            <JoyLink
              href="/contact"
              underline="none"
              sx={{
                display: "block",
                width: "100%",
                mt: 2,
                bgcolor:
                  pathname === "/contact" ? colors.orange : colors.safety,
                color: colors.white,
                py: "0.75rem",
                ...monoLabelStyle,
                textAlign: "left",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: colors.orange,
                  color: colors.white,
                },
              }}
            >
              Request a Quote
            </JoyLink>
          </Stack>
        </Sheet>
      )}
    </Box>
  );
}
